/**
 * Load external script once globally
 * 
 * Ensures scripts like TikTok embed.js or Instagram embed.js are only loaded once,
 * even if multiple components request them.
 */

const loadedScripts = new Set<string>();
const loadingPromises = new Map<string, Promise<void>>();

export function loadScriptOnce(src: string): Promise<void> {
  // Already loaded
  if (loadedScripts.has(src)) {
    return Promise.resolve();
  }

  // Currently loading
  if (loadingPromises.has(src)) {
    return loadingPromises.get(src)!;
  }

  // Start loading
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    
    script.onload = () => {
      loadedScripts.add(src);
      loadingPromises.delete(src);
      resolve();
    };
    
    script.onerror = () => {
      loadingPromises.delete(src);
      reject(new Error(`Failed to load script: ${src}`));
    };
    
    document.body.appendChild(script);
  });

  loadingPromises.set(src, promise);
  return promise;
}
